import React from "react";
import { useNavigate } from "react-router-dom";
import "./LegalDocument.css";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <main className="legal-page">
      <button type="button" className="legal-back-button" onClick={() => navigate(-1)}>
        Back
      </button>

      <header className="legal-header">
        <p className="legal-kicker">Privacy</p>
        <h1>BlueWire Privacy Policy</h1>
        <p className="legal-effective"><strong>Effective Date:</strong> 09/20/2025</p>
      </header>

      <section className="legal-section">
        <p>
          At BlueWire, we are committed to protecting the privacy and security of our users.
          This Privacy Policy explains what information we collect, how we use it, and the
          choices you have regarding your data.
        </p>
      </section>

      <section className="legal-section">
        <h2>Information We Collect</h2>
        <p>When you create an account and use BlueWire, we may collect the following information:</p>
        <ul>
          <li>Name, work email address, and phone number</li>
          <li>Agency, rank, and assignment</li>
          <li>Verification data (see Identity Verification section below)</li>
          <li>Notes you create (private or shared)</li>
          <li>Messages and BOLO alerts you send or receive</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Identity Verification</h2>
        <p>
          To maintain a secure and trusted environment, BlueWire requires identity verification.
          This may include submission of a department-issued email address, a department-issued
          identification credential, and a real-time selfie.
        </p>
        <p>
          We may use automated technologies, including facial comparison, to confirm that the
          individual submitting identification matches the person registering the account.
        </p>
        <p>
          Verification images are used solely for identity confirmation and platform security.
          They are not used for advertising, profiling, or any unrelated commercial purposes.
        </p>
      </section>

      <section className="legal-section">
        <h2>Private Profile Notes</h2>
        <p>
          If you use Private Profile Notes, the content you enter is stored by BlueWire
          and associated with your account and the profile where the note was created.
          These notes are designed to be visible only to you within the app. BlueWire
          may access or disclose Private Profile Notes when necessary to operate the
          service, provide support, enforce our terms, protect safety or security, or
          comply with legal obligations.
        </p>
      </section>

      <section className="legal-section">
        <h2>How We Use Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Authenticate and verify your account</li>
          <li>Confirm eligibility and agency affiliation</li>
          <li>Connect you with other verified users (searchable by agency, rank, assignment)</li>
          <li>Enable messaging, BOLO alerts, and shared notes</li>
          <li>Deliver app features such as push notifications and search</li>
          <li>Maintain platform security and detect fraudulent or unauthorized activity</li>
        </ul>
        <p>Your data is never sold, rented, or used for advertising or unrelated commercial purposes.</p>
      </section>

      <section className="legal-section">
        <h2>Sharing of Information</h2>
        <p>
          We do not share your personal information with third parties except as necessary to
          provide BlueWire's services. Specifically:
        </p>
        <ul>
          <li>Firebase provides authentication, secure data storage, and push notifications</li>
          <li>Algolia provides search functionality to return relevant user and content results</li>
        </ul>
        <p>
          These providers process data only to support BlueWire functionality and do not use it
          for independent purposes.
        </p>
        <p>We do not provide user data to any third party for marketing or advertising.</p>
      </section>

      <section className="legal-section">
        <h2>Data Retention and Deletion</h2>
        <p>
          Verification images, including identification documents and selfies, are stored
          temporarily and are automatically deleted within approximately 96 hours after
          verification is completed.
        </p>
        <p>
          Verification metadata (such as confirmation status and confidence scores) may be
          retained for a limited period (generally up to 90 days) for auditing and security purposes.
        </p>
        <p>Your account data is retained for as long as your account is active.</p>
        <p>
          If you delete a message, it is removed from all participants and permanently deleted
          from our systems.
        </p>
        <p>
          If you delete your account, personal profile information and verification data are deleted.
          Certain content (such as messages or notes shared with others) may remain visible to other
          users where it has already been distributed within the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>User Rights</h2>
        <p>You may:</p>
        <ul>
          <li>Edit or update your profile information within the app</li>
          <li>Delete your messages, notes, or your entire account at any time</li>
          <li>
            Contact us for assistance with your data at{" "}
            <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>
          </li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Security</h2>
        <p>
          We implement appropriate technical and organizational safeguards to protect your data.
          All data is encrypted in transit and stored securely. Access is restricted to authenticated
          users.
        </p>
      </section>

      <section className="legal-section">
        <h2>Children's Privacy</h2>
        <p>
          BlueWire is intended only for verified law enforcement and public safety personnel who are
          21 years of age or older. We do not knowingly collect information from children. If we
          become aware of such data, it will be deleted promptly.
        </p>
      </section>

      <section className="legal-section">
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Any changes will be posted at this
          URL, and continued use of BlueWire constitutes acceptance of the updated policy.
        </p>
      </section>
    </main>
  );
}
